import { useEffect, useRef } from 'react';

/**
 * Hook de auto-logout por inatividade.
 *
 * Padrão da indústria: 30 min para apps de gestão. O timer reseta em qualquer
 * interação (mouse, teclado, toque, scroll). Quando expira, dispara onTimeout().
 *
 * @param {function} onTimeout - callback executado quando o tempo de inatividade é atingido
 * @param {boolean} enabled - se false, o timer não roda (útil para tela de login)
 * @param {number} timeoutMs - tempo de inatividade em ms (padrão 30 min)
 */
export function useIdleTimeout(onTimeout, enabled = true, timeoutMs = 30 * 60 * 1000) {
  const timerRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);

  // Mantém ref sempre com a versão mais recente do callback (evita stale closure)
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    if (!enabled) return;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onTimeoutRef.current?.();
      }, timeoutMs);
    };

    // Eventos que contam como atividade
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }));

    // Inicia o timer no mount
    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, [enabled, timeoutMs]);
}

export default useIdleTimeout;
